import { ProductList } from "@packages/product-list/ProductList";
import { StandardProps } from "@/types/types";
import { transformProduct } from "@/utils/transform";
import { useExperienceString, useFeatureFlags } from "@/flags/flags";
import { useEffect } from "react";
import { LoadingSpinner } from "@JohnLewisPartnership/jl-design-system/dist/components/loading-spinner/LoadingSpinner";
import { Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import styles from "./ProductGrid.module.scss";
import { useRouteParams } from "@/hooks/useRouteParams";
import { useHasComeIntoView } from "@/hooks/useHasComeIntoView";
import { useChunkLoader } from "@/contexts/ProductListingDataContext/ProductListingDataContext";
import Head from "next/head";
import { generateSrcSet } from "@packages/product-list/generateSrcSet";
import { applyFeatureFlagOverrides } from "@packages/feature-flags";

export function ProductGrid({
  productListingData,
  productListProps,
}: Pick<StandardProps, "productListingData" | "productListProps">) {
  const flags = useFeatureFlags();
  const {
    ref: chunkLoadingSentinelRef,
    hasComeIntoView: chunkLoadingSentinelhasComeIntoView,
  } = useHasComeIntoView({ rootMargin: "1800px" });
  const route = useRouteParams();
  const experiencesRawString = useExperienceString();
  const { loadNextChunk, isLoadingChunk, isChunkLoadError } = useChunkLoader();
  const numericPageSize = Number(flags.pageSize?.value);
  const numericCurrentPage = Number(route.query.page || 1);

  const products = productListingData.products.map((product) =>
    transformProduct(product),
  );

  useEffect(
    function loadNextChunkWhenSentinelIsVisible() {
      if (chunkLoadingSentinelhasComeIntoView && !isChunkLoadError) {
        loadNextChunk();
      }
    },
    [chunkLoadingSentinelhasComeIntoView, loadNextChunk, isChunkLoadError],
  );

  const statefulProductListFlags = applyFeatureFlagOverrides({
    baseFlags: productListProps.flags,
    parameterFlags: {
      largeCards: flags.isLargeCardSizeFlagOn?.value,
      fullWidth: true,
    },
  });

  const { src, srcSet } = generateSrcSet(
    products[0]?.variants[0]?.images.primary || "",
  );

  return (
    <>
      <Head>
        <link
          rel="preload"
          as="image"
          fetchPriority="high"
          href={src}
          imageSrcSet={srcSet}
        />
      </Head>
      <div
        id="main-content"
        data-testid="component-grid-container"
        aria-label="Product listing page results"
        data-list-config={productListProps.config.name}
      >
        <meta
          name="total-number-of-items"
          content={products.length.toString()}
        />
        <ProductList
          products={products}
          resultsInfo={{
            start: (numericCurrentPage - 1) * numericPageSize,
            end: numericCurrentPage * numericPageSize,
            pageSize: numericPageSize,
            total: productListingData.results,
            chunkSize: Number(flags.chunkSize?.value),
          }}
          flags={statefulProductListFlags}
          config={productListProps.config}
          experiencesRawString={experiencesRawString}
          consumerId="web-plp-scaffold-ui"
        />
        {isChunkLoadError && (
          <Message
            type="error"
            title="Error loading the next set of products, tap 'Load more products' to try again."
            buttonText="Load more products"
            buttonProps={{
              onClick: loadNextChunk,
            }}
            className={styles.chunkLoadErrorMessage}
          />
        )}
        <div data-testid="chunkLoadingSentinel" ref={chunkLoadingSentinelRef}>
          {isLoadingChunk && (
            <div className={styles.chunkLoading}>
              <LoadingSpinner />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
